import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap, take, takeWhile } from 'rxjs/operators';
import { Auth } from '@angular/fire/auth';

import { HOST_AUTH_SERVICE } from '@dibsondance/authentication-host';
import {
  AUTH_SERVICE,
  FirebaseAuthenticationService,
} from '@dibsondance/shared-authentication-base';
import { LoggerService } from '@dibsondance/shared-logging';
import { ROUTE_PATHS } from '../app.routes.config';

@Injectable()
export class AuthGuard implements CanActivate {
  private readonly authService = inject(HOST_AUTH_SERVICE);
  private readonly router = inject(Router);
  private readonly auth = inject(Auth);
  private readonly firebaseAuthService = inject(
    AUTH_SERVICE
  ) as FirebaseAuthenticationService;
  private readonly logger = inject(LoggerService);

  canActivate(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _route: ActivatedRouteSnapshot,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Wait for Firebase auth state to be restored, then wait for ID token
    return this.waitForAuthInitialization().pipe(
      switchMap(() => this.waitForIdToken()),
      map(() => {
        // Check the current authentication state
        const isAuthenticated = this.authService.isLoggedIn();
        this.logger.info(
          '🛡️ Authentication check',
          { isAuthenticated },
          '[AUTH-GUARD]'
        );

        if (!isAuthenticated) {
          this.logger.info(
            '🛡️ Not authenticated, redirecting to login',
            undefined,
            '[AUTH-GUARD]'
          );
          return this.router.createUrlTree(['/', ROUTE_PATHS.LOGIN]);
        }

        // User is authenticated, allow access to authenticated area
        this.logger.info(
          '🛡️ User authenticated, allowing access',
          undefined,
          '[AUTH-GUARD]'
        );
        return true;
      })
    );
  }

  private waitForAuthInitialization(): Observable<void> {
    // If user is already available, return immediately
    if (this.auth.currentUser !== null) {
      return of(undefined);
    }

    // Otherwise, wait for the auth state to be restored
    return new Observable<void>((subscriber) => {
      const unsubscribe = this.auth.onAuthStateChanged(() => {
        subscriber.next();
        subscriber.complete();
      });

      // Fallback timeout in case something goes wrong
      const timeout = setTimeout(() => {
        subscriber.next();
        subscriber.complete();
      }, 2000); // 2 second timeout

      // Cleanup
      return () => {
        unsubscribe();
        clearTimeout(timeout);
      };
    }).pipe(take(1));
  }

  private waitForIdToken(): Observable<void> {
    // If token is already available, return immediately
    if (this.firebaseAuthService.idToken()) {
      this.logger.debug(
        '🛡️ ID token already available',
        undefined,
        '[AUTH-GUARD]'
      );
      return of(undefined);
    }

    this.logger.debug('🛡️ Waiting for ID token...', undefined, '[AUTH-GUARD]');

    // Poll for the token to be available using timer
    return timer(0, 100).pipe(
      map(() => this.firebaseAuthService.idToken()),
      takeWhile((token) => !token, true), // Include the first truthy value
      take(30), // Maximum 30 checks (3 seconds at 100ms intervals)
      map((token) => {
        if (token) {
          this.logger.debug(
            '🛡️ ID token is now available',
            undefined,
            '[AUTH-GUARD]'
          );
        } else {
          this.logger.warn(
            '🛡️ ID token wait timeout, proceeding anyway',
            undefined,
            '[AUTH-GUARD]'
          );
        }
        return undefined;
      })
    );
  }
}
